import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Gauge, Brain, BarChart3, Fuel, Zap, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import ihelmPlatform from "@/assets/ihelm-platform-shadow.png";
import cetafuelVisual from "@/assets/cetafuel-homepage.png";

const SolutionSection = () => {
  const { t } = useTranslation('home');

  const ihelmFeatures = [
    { icon: Gauge, title: t('solution.ihelmFeature1Title'), description: t('solution.ihelmFeature1Desc') },
    { icon: Brain, title: t('solution.ihelmFeature2Title'), description: t('solution.ihelmFeature2Desc') },
    { icon: BarChart3, title: t('solution.ihelmFeature3Title'), description: t('solution.ihelmFeature3Desc') },
  ];

  const cetafuelFeatures = [
    { icon: Fuel, title: t('solution.cetafuelFeature1Title'), description: t('solution.cetafuelFeature1Desc') },
    { icon: Shield, title: t('solution.cetafuelFeature2Title'), description: t('solution.cetafuelFeature2Desc') },
    { icon: Zap, title: t('solution.cetafuelFeature3Title'), description: t('solution.cetafuelFeature3Desc') },
  ];

  return (
    <>
      <section className="py-20 md:py-28 bg-surface-light">
        <div className="container mx-auto px-6">
          <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5 }} className="text-xs font-semibold tracking-widest uppercase text-primary text-center mb-16">
            {t('solution.label')}
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="flex flex-col justify-center">
              <p className="text-xs font-semibold tracking-widest text-foreground mb-4">{t('solution.ihelmLabel')}</p>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-6">{t('solution.ihelmHeading')}</h3>
              <ul className="space-y-5 mb-8">
                {ihelmFeatures.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />
                    <div>
                      <h4 className="font-display font-semibold text-foreground text-sm mb-0.5">{feature.title}</h4>
                      <p className="text-xs text-foreground/60 leading-relaxed">{feature.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div>
                <Link to="/ihelm-intelligent-platform" className="inline-flex items-center gap-2 border border-[hsl(var(--secondary-border-light))] text-[hsl(var(--secondary-border-light))] bg-secondary/18 px-6 py-3 rounded-md font-medium text-sm hover:bg-secondary/25 transition-colors">
                  {t('solution.ihelmCta')} <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.1 }} className="relative flex items-center justify-center">
              <img src={ihelmPlatform} alt="iHelm platform" className="w-full h-auto object-contain" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-surface-light">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="rounded-2xl overflow-hidden flex items-center justify-center order-2 md:order-1">
              <img src={cetafuelVisual} alt="CetaFuel virtual fuel sensing" className="w-full h-full object-cover rounded-2xl" loading="lazy" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.1 }} className="flex flex-col justify-center order-1 md:order-2">
              <p className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4">{t('solution.cetafuelLabel')}</p>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-6">{t('solution.cetafuelHeading')}</h3>
              <ul className="space-y-5 mb-8">
                {cetafuelFeatures.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />
                    <div>
                      <h4 className="font-display font-semibold text-foreground text-sm mb-0.5">{feature.title}</h4>
                      <p className="text-xs text-foreground/60 leading-relaxed">{feature.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div>
                <Link to="/cetafuel" className="inline-flex items-center gap-2 border border-[hsl(var(--secondary-border-light))] text-[hsl(var(--secondary-border-light))] bg-secondary/18 px-6 py-3 rounded-md font-medium text-sm hover:bg-secondary/25 transition-colors">
                  {t('solution.cetafuelCta')} <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SolutionSection;
